import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  InputBase,
  Typography,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import API from "../api";

const Talent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responseError, setResponseError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "olivia@untitledui.com",
    country_id: "",
    state_id: "",
    zipcode: "",
  });

  const [errors, setErrors] = useState({
    name: false,
    phone: false,
    email: false,
    country_id: false,
    state_id: false,
    zipcode: false,
  });

  const handleInputChange = (e) => {
    setResponseError(null);
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Handle different field types for validation
    let isValid = false;
    if (name === "country_id" || name === "state_id") {
      // For dropdown fields, check if value is selected
      isValid = value !== "";
    } else {
      // For text fields, check if trimmed value is not empty
      isValid = value.trim() !== "";
    }

    setErrors((prevState) => ({
      ...prevState,
      [name]: !isValid,
    }));
  };

  const handlePhoneChange = (value) => {
    setResponseError(null);
    setFormData((prevState) => ({
      ...prevState,
      phone: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      phone: !value,
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseError(null);
    const newErrors = {
      name: formData.name.trim() === "",
      phone: !formData.phone,
      email: formData.email.trim() === "",
      country_id: formData.country_id === "",
      state_id: formData.state_id === "",
      zipcode: formData.zipcode.trim() === "",
    };

    setErrors(newErrors);

    const isFormValid = Object.values(newErrors).every((error) => !error);

    if (isFormValid) {
      setIsLoading(true);
      try {
        const formObj = new FormData();
        formObj.append("name", formData.name.trim());
        formObj.append("phone", formData.phone);
        formObj.append("email", formData.email.trim());
        formObj.append("country_id", formData.country_id);
        formObj.append("state_id", formData.state_id);
        formObj.append("zipcode", formData.zipcode.trim());

        const resp = await API.post("/api/lead-client", formObj);
 
        
        if (resp?.data?.success) {
          setSnackbarMessage("Lead client registered successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          
          // Reset form
          setFormData({
            name: "",
            phone: "",
            email: "olivia@untitledui.com",
            country_id: "",
            state_id: "",
            zipcode: "",
          });
        } else {
          setResponseError(resp?.data?.msg || "Registration failed");
          setSnackbarMessage(resp?.data?.msg || "Registration failed");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
        
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        const errorMsg = error?.response?.data?.msg || "An error occurred. Please try again.";
        setResponseError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.log(error);
      }
    }
  };

  const getLocations = async () => {
    try {
      if (!formData?.country_id) {
        setStates([]);
        return;
      }
      const resp = await API.get(`/api/get-states/${formData?.country_id}`);
      if (resp?.data?.success) {
        setStates(resp?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getLocations();
  }, [formData?.country_id]);

  useEffect(() => {
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            )
              .then((response) => response.json())
              .then((data) => {
                setCountry(data.countryCode);
                setLoading(false);
              })
              .catch((err) => {
                setError("Failed to fetch country data");
                setLoading(false);
              });
          },
          (error) => {
            setError("Failed to get location");
            setLoading(false);
          }
        );
      } else {
        setError("Geolocation is not supported by this browser.");
        setLoading(false);
      }
    };

    getLocation();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCountries = async () => {
      setCountriesLoading(true);
      setCountriesError(null);
      try {
        const resp = await API.get("/api/get-countries");
        if (cancelled) return;

        if (resp?.data?.success) {
          setCountries(Array.isArray(resp?.data?.data) ? resp.data.data : []);
        } else {
          setCountries([]);
          setCountriesError(resp?.data?.msg || "Failed to load countries");
        }
      } catch (err) {
        if (cancelled) return;
        setCountries([]);
        setCountriesError(
          err?.response?.data?.msg || "Failed to load countries"
        );
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    };

    fetchCountries();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F0F4F8" }}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Column - Image */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: { xs: "none", md: "flex" },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
       
            src="https://projects.tangiblethemes.com/Shglocums/wp-content/uploads/2025/08/Find-talent.jpg"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contained",
            }}
          />
          {/* Text Overlay */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(to bottom, transparent, #21404B99)",
              p: 4,
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "24px", xl: "36px" },
                lineHeight: { xs: "32px", xl: "44px" },
                mb: 2,
                maxWidth: "500px",
              }}
            >
        Find the Right Healthcare Talent – Fast
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", xl: "20px" },
                lineHeight: { xs: "24px", xl: "30px" },
                opacity: 0.9,
              }}
            >
       Looking for qualified medical professionals to fill short-term or urgent vacancies?<br/> Our network of vetted providers is ready to step in and deliver exceptional patient care.
       Fill out the form to connect with top-tier locum doctors, nurses, and specialists who match your needs. Whether it’s to cover a shift, a week, or longer, we’ll ensure you get the right talent, at the right time.  </Typography>
          </Box>
        </Grid>

        {/* Right Column - Form */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "white",
            p: { xs: 2, md: 4 },
            justifyContent:'start',
            alignItems:'start',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2rem", xl: "2.5rem" },
              fontFamily: "DM Serif Text",
              color: "#002357",
              mb: 4,
              textAlign: "center",
            }}
          >
            Talent
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width:'100%',
            }}
          >
            {/* Full Name */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Full Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <InputBase
                fullWidth
                placeholder="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                sx={{
                  bgcolor: "#F5F5F5",
                  border: errors.name && "1px solid red !important",
                  height: "46px",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontSize: "15px",
                  p: "8px 12px 8px 16px",
                  "&:hover": {
                    border: "1px solid #2a9d8f !important",
                  },
                }}
              />
              {errors.name && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Full name is required
                </span>
              )}
            </Box>

            {/* Phone Number */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Phone number <span style={{ color: "red" }}>*</span>
              </Typography>
              <PhoneInput
                international
                placeholder="000-0000"
                value={formData.phone}
                onChange={handlePhoneChange}
                style={{
                  paddingLeft: "1rem",
                  borderRadius: "8px",
                  height: "46px",
                  color: "black",
                  background: "#F5F5F5",
                }}
                className="phone-input"
                defaultCountry={country}
              />
              {errors.phone && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Phone number is required
                </span>
              )}
            </Box>

            {/* Email Address */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Email Address <span style={{ color: "red" }}>*</span>
              </Typography>
              <InputBase
                fullWidth
                placeholder="olivia@untitledui.com"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{
                  bgcolor: "#F5F5F5",
                  border: errors.email && "1px solid red !important",
                  height: "46px",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontSize: "15px",
                  p: "8px 12px 8px 16px",
                  "&:hover": {
                    border: "1px solid #2a9d8f !important",
                  },
                }}
              />
              {errors.email && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Email is required
                </span>
              )}
            </Box>

            {/* Country */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Country <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="country_id"
                  value={formData.country_id}
                  onChange={handleInputChange}
                  error={errors.country_id}
                  disabled={countriesLoading}
                  sx={{
                    height: "46px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    bgcolor: "#F5F5F5",
                    fontSize: "15px",
                    "&:hover": {
                      border: "none",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #2a9d8f",
                      outline: "none",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value="">Select country</MenuItem>
                  {countriesLoading ? (
                    <MenuItem disabled value="">
                      Loading...
                    </MenuItem>
                  ) : countriesError ? (
                    <MenuItem disabled value="">
                      Failed to load countries
                    </MenuItem>
                  ) : (
                    countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.country_id && (
                  <span style={{ color: "red", fontSize: "0.7rem" }}>
                    Country is required
                  </span>
                )}
              </FormControl>
            </Box>

            {/* State */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                State <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleInputChange}
                  error={errors.state_id}
                  sx={{
                    height: "46px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    bgcolor: "#F5F5F5",
                    fontSize: "15px",
                    "&:hover": {
                      border: "none",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #2a9d8f",
                      outline: "none",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {states?.length > 0
                    ? states.map((state, index) => (
                        <MenuItem key={index} value={state?.id}>
                          {state?.name}
                        </MenuItem>
                      ))
                    : null}
                </Select>
                {errors.state_id && (
                  <span style={{ color: "red", fontSize: "0.7rem" }}>
                    State is required
                  </span>
                )}
              </FormControl>
            </Box>

            {/* Zip Code */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Zip Code <span style={{ color: "red" }}>*</span>
              </Typography>
              <InputBase
                fullWidth
                placeholder="Enter zip code"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleInputChange}
                sx={{
                  bgcolor: "#F5F5F5",
                  border: errors.zipcode && "1px solid red !important",
                  height: "46px",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontSize: "15px",
                  p: "8px 12px 8px 16px",
                  "&:hover": {
                    border: "1px solid #2a9d8f !important",
                  },
                }}
              />
              {errors.zipcode && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Zip code is required
                </span>
              )}
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 2,
                textTransform: "none",
                color: "#292A2F",
                fontSize: "1rem",
                width:'171px',
                fontWeight: 600,
                bgcolor: "#E9C369",
                borderRadius: "10px",
                height: "48px",
                "&:hover": {
                  bgcolor: "#E9C369",
                  color: "#292A2F",
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Submit Request"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Talent; 